import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import { ResponsiveContainer, AreaChart, Area } from "recharts";
import api from "@/lib/api";
import { buildOption, buildChoropleth, buildBubbleMap, fitGeoView, labelsFromMeta, formatNumber, formatValue, applyLegendOptions, setChartFont, PALETTE } from "@/lib/chartTheme";
import { setLanguage, t } from "@/lib/i18n";
import { DynamicTextView } from "@/components/bi/DynamicText";
import { AiDrillButton } from "@/components/bi/AiDrillDialog";
import { buildSemanticQuery, buildModelQuery, GEO_TYPES, DRILLDOWN_TYPES, EXT_TYPES, CANVAS_TYPES, HIGHLIGHT_TYPES } from "@/lib/widgetCatalog";
import { applySortEngine, sortLevels } from "@/lib/sortEngine";
import { dimOutNames } from "@/lib/dimKeys";
import { applyChartSort, applyDateLabels, applyTooltipStyle, applyHighlightToOption, activeRanges, rangeStyleOf, rangeLegendSeries, applyRefLines, applyTrendline, applySeriesStyles, applyAnomalyMarkers, advColorMode, advStyleResolver } from "@/lib/chartData";
import { ensureWorldMap, ensureCountryMap, ensureCustomMap, getCentroids, getRegionNames, countryMapInfo, ensureCityCentroids, lookupCity, regionMatchKey } from "@/lib/geo";
import { Loader2, AlertTriangle, Inbox, ChevronRight, CornerUpLeft, Sparkles, Lock } from "lucide-react";
import { KpiView } from "@/components/bi/KpiCard";
import { TableView } from "@/components/bi/DataTable";
import { ChartA11y } from "@/components/bi/ChartA11y";
import { ChartKeyboardNav } from "@/components/bi/ChartKeyboardNav";
import { DrillTargetMenu } from "@/components/bi/DrillTargetMenu";
import { normalizeDrillthrough, buildDrillContext, navigateDrill, targetAvailability } from "@/lib/drillthrough";
import { DrillthroughModal } from "@/components/bi/DrillthroughModal";
import { useA11y } from "@/context/A11yContext";
import { PivotTable } from "@/components/bi/PivotTable";
import { splitPivotDimObjects } from "@/lib/pivotEngine";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PerspectiveWidget } from "./PerspectiveWidget";
import { federatedSpecOf, federatedPayload } from "@/lib/crossSource";
import { ImageWidget } from "./ImageWidget";
import { MultiFilterWidget } from "./MultiFilterWidget";
import { ComboBoxFilterWidget, ListBoxFilterWidget, DateFilterWidget, SlicerFilterWidget } from "./FilterWidgets";
import { applyQuickCalcs } from "@/lib/quickCalcs";
import { expandTableInstances } from "@/lib/tableColumns";
import { reportClientError, logWidgetPerf } from "@/lib/diagnostics";
import { fetchShared } from "@/lib/queryCache";
import { WidgetSkeleton } from "@/components/bi/WidgetSkeleton";
import { ScenarioGridWidget } from "@/components/bi/ScenarioCompare";
import { RichTextWidget } from "@/components/bi/RichTextWidget";
import { ChartGridView, isChartGrid } from "@/components/bi/ChartGrid";
import { InsightStrip, InsightToggle } from "@/components/bi/InsightStrip";
import { buildInsights, INSIGHT_TYPES } from "@/lib/insights";

// Lazy-loaded Data Grid (TanStack table + virtualizer only on demand)
const DataGridWidget = React.lazy(() => import("./DataGridWidget").then((m) => ({ default: m.DataGridWidget })));

// Lazy-loaded Leaflet map (bundles leaflet + markercluster only on demand)
const PointMap = React.lazy(() => import("./PointMap").then((m) => ({ default: m.PointMap })));
const GoogleMapWidget = React.lazy(() => import("./GoogleMapWidget"));

// Lazily load + register heavy ECharts extensions (echarts-gl / wordcloud / liquidfill)
// only when a widget actually needs them. The webpack alias (echarts$ -> dist/echarts.js)
// guarantees the dynamically-loaded chunk registers onto the SAME shared echarts instance,
// so the base bundle stays small without breaking the single-instance requirement.
let extPromise = null;
function loadExtensions() {
  if (!extPromise) extPromise = import("@/lib/echartsSetup");
  return extPromise;
}

// Items 8/9: chart families whose x-axis is a timeline — sorted chronologically unless the user overrides.
// Item 35: pie/doughnut follow the hierarchy order too when their dimension is a date level.
const TIME_SORT_TYPES = ["line", "area", "combo", "bar", "bar_stacked", "candlestick", "calendar_heatmap",
  "pie", "donut", "semi_donut"];

function dimOut(d) {
  if (!d) return null;
  if (d.key) return d.key;
  if (d.alias) return d.alias;
  if (d.date_part && d.date_part !== "none") return `${d.field}_${d.date_part}`;
  return d.field;
}

// Item 35: instance-aware column names (two hierarchy levels on one date field).
const dimKeyAt = (dims, i) => dimOutNames(dims)[i] || null;

// Merge per-widget measure format overrides (config.measures[i].format) into query result meta
function withFormatOverrides(data, config) {
  if (!data) return data;
  const ov = {};
  (config.measures || []).forEach((m) => {
    const k = m.key || m.alias || `${m.agg || "sum"}_${m.field}`;
    if (m.format && Object.keys(m.format).length) ov[k] = m.format;
  });
  if (!Object.keys(ov).length) return data;
  const meas = [...(((data.meta || {}).measures) || [])];
  const have = new Set(meas.map((m) => m.key));
  meas.forEach((m, i) => { if (ov[m.key]) meas[i] = { ...m, format: { ...(m.format || {}), ...ov[m.key] } }; });
  Object.keys(ov).forEach((k) => { if (!have.has(k)) meas.push({ key: k, format: ov[k] }); });
  return { ...data, meta: { ...(data.meta || {}), measures: meas } };
}

const EMPTY_TOKENS = { dash: "\u2014", zero: "0", blank: "" };

// Batch 8: widget types that support baseline-vs-scenario what-if comparison
const WHATIF_COMPARE_TYPES = ["kpi", "bar", "bar_stacked", "line", "area", "combo"];

function escapeHtml(s) { return String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c])); }

// Item 55: data-rich configurable tooltip (multi-metric, formatted, comparative)
function richTooltipFormatter({ data, spec, config, ttCfg }) {
  const rows = (data && data.rows) || [];
  const metaMeas = ((data && data.meta) || {}).measures || [];
  const formats = {}; const labelToKey = {};
  metaMeas.forEach((m) => { if (m.key) { formats[m.key] = m.format; if (m.label) labelToKey[m.label] = m.key; } });
  const xKey = spec.x_axis;
  const yKeys = spec.y_axis || [];
  const seriesKey = spec.series || null;
  const rowByCat = {};
  if (!seriesKey) rows.forEach((r) => { rowByCat[String(r[xKey])] = r; });
  const ttMeasures = config.tooltip_measures || [];
  const renames = Object.fromEntries((ttCfg.labels || []).filter((l) => l.name && l.label).map((l) => [l.name, l.label]));
  const dispName = (n) => renames[n] || n;
  const fmtFor = (name, idx) => formats[name] || formats[labelToKey[name]] || (!seriesKey ? formats[yKeys[idx]] || formats[yKeys[0]] : formats[yKeys[0]]);
  const val = (p) => {
    const v = Array.isArray(p.value) ? p.value[p.value.length - 1] : (p.value ?? (p.data && p.data.value) ?? p.data);
    return Number(v);
  };
  return (params) => {
    const arr = Array.isArray(params) ? params : [params];
    const cat = arr[0]?.axisValueLabel ?? arr[0]?.name ?? "";
    const total = arr.reduce((a, p) => { const v = val(p); return a + (Number.isFinite(v) ? v : 0); }, 0);
    let html = `<div style="font-weight:600;margin-bottom:4px">${escapeHtml(ttCfg.header || String(cat))}</div>`;
    arr.forEach((p, i) => {
      const v = val(p);
      if (!Number.isFinite(v)) return;
      const share = ttCfg.share && total ? ` <span style="opacity:.65">(${((v / total) * 100).toFixed(1)}%)</span>` : "";
      html += `<div style="display:flex;align-items:center;gap:6px;margin:2px 0">${p.marker || ""}<span>${escapeHtml(dispName(p.seriesName || ""))}</span><span style="margin-left:auto;padding-left:14px;font-weight:600">${formatValue(v, fmtFor(p.seriesName, p.seriesIndex ?? i))}${share}</span></div>`;
    });
    if (!seriesKey && ttMeasures.length) {
      const row = rowByCat[String(cat)];
      if (row) {
        let extra = "";
        ttMeasures.forEach((t) => {
          const k = `tt_${t.agg}_${t.field}`;
          if (row[k] !== undefined) extra += `<div style="display:flex;gap:10px"><span style="opacity:.75">${escapeHtml(t.label || `${t.agg.replace("_", " ")} of ${t.field}`)}</span><span style="margin-left:auto;font-weight:600">${formatNumber(row[k])}</span></div>`;
        });
        if (extra) html += `<div style="border-top:1px solid rgba(120,130,160,.25);margin-top:4px;padding-top:4px">${extra}</div>`;
      }
    }
    return html;
  };
}
function measOut(m) {
  if (!m) return null;
  if (m.key) return m.key;
  return m.alias || `${m.agg || "sum"}_${m.field}`;
}

// Forecast overlay (Item 161): extend the x-axis with future periods, add a dashed
// forecast series + a soft confidence band. History series keep their length (gaps).
function applyForecastToOption(option, fc) {
  const xa = Array.isArray(option.xAxis) ? option.xAxis[0] : option.xAxis;
  if (!xa || !Array.isArray(xa.data) || !Array.isArray(option.series) || !option.series.length) return;
  const histLen = xa.data.length;
  const pts = fc.forecast || [];
  if (!pts.length) return;
  xa.data = [...xa.data, ...pts.map((p) => p.period)];
  const base = option.series[0];
  const baseData = Array.isArray(base.data) ? base.data : [];
  const rawLast = baseData.length ? baseData[baseData.length - 1] : null;
  const lastVal = rawLast && typeof rawLast === "object" ? rawLast.value : rawLast;
  const pad = (n) => Array(n).fill(null);
  const keepNames = option.series.map((s) => s.name).filter(Boolean);
  option.series = [
    ...option.series,
    { name: "Forecast", type: "line", data: [...pad(Math.max(histLen - 1, 0)), lastVal, ...pts.map((p) => p.value)],
      lineStyle: { type: "dashed", width: 2, color: "#8b5cf6" }, itemStyle: { color: "#8b5cf6" }, symbol: "circle", symbolSize: 5, z: 5, smooth: base.smooth },
    { name: "__fc_lo", type: "line", stack: "__fc_ci", data: [...pad(histLen), ...pts.map((p) => p.lo)],
      lineStyle: { opacity: 0 }, symbol: "none", stackStrategy: "all", silent: true, tooltip: { show: false } },
    { name: "__fc_band", type: "line", stack: "__fc_ci", data: [...pad(histLen), ...pts.map((p) => p.hi - p.lo)],
      lineStyle: { opacity: 0 }, areaStyle: { color: "rgba(139,92,246,0.12)" }, symbol: "none", stackStrategy: "all", silent: true, tooltip: { show: false } },
  ];
  option.legend = { ...(option.legend || {}), data: [...keepNames, "Forecast"] };
}

async function runQuery({ datasetId, semQuery, publicToken, signal }) {
  if (publicToken) {
    const { data } = await api.post("/public/model/query", { token: publicToken, dataset_id: datasetId, semantic_query: semQuery }, { timeout: 45000, signal });
    return data;
  }
  const { data } = await api.post(`/datasets/${datasetId}/model/query`, semQuery, { timeout: 45000, signal });
  return data;
}

const isCancel = (e) => e?.code === "ERR_CANCELED" || e?.name === "CanceledError";

// Item 192.4: exponential backoff for transient failures (network / 5xx / timeout).
async function runWithRetry(fn, onRetry) {
  let lastErr;
  for (let attempt = 0; attempt < 3; attempt++) {
    try { return await fn(); } catch (e) {
      lastErr = e;
      if (isCancel(e)) throw e;
      const st = e?.response?.status;
      const transient = !e.response || st >= 500 || e.code === "ECONNABORTED";
      if (!transient || attempt === 2) throw e;
      onRetry?.(attempt + 1);
      await new Promise((r) => setTimeout(r, 700 * Math.pow(2, attempt)));
    }
  }
  throw lastErr;
}

export function WidgetRenderer(props) {
  return <MemoWidgetRenderer {...props} />;
}

function WidgetRendererInner({ widget, globalFilters = [], highlightFilters = [], publicToken = null, onSlicerChange, slicerValue, filterValue, onFilterChange, multiFilterValues = {}, refreshToken = 0, onCrossFilter = null, onConfigChange = null, onDrillthrough = null, pivotEngine = "client", whatIfParams = null, whatIfBaseline = null, whatIfCompare = false, whatIfDefs = [], whatIfScenarios = [], dashboardId = null, dashboardName = null, drillTrail = null, onConfigPatch = null, chartFont, lang, onBandDrill = null }) {
  const { type, config = {}, dataset_id } = widget;
  // Dashboard-level chart font + UI language: sync before options/labels are built in this render pass.
  if (chartFont !== undefined) setChartFont(chartFont);
  if (lang !== undefined) setLanguage(lang);
  const { prefs: a11yPrefs } = useA11y();
  const [dtMenu, setDtMenu] = useState(null); // multi-target drillthrough picker {x, y, targets, run}
  const [dtModal, setDtModal] = useState(null); // item 13: "open in modal" drillthrough
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [retryToken, setRetryToken] = useState(0);
  const retryCountRef = useRef(0);
  // Item 192.5: viewport-based lazy init (disabled for headless/snapshot renders)
  const lazyEligible = typeof navigator !== "undefined" && !navigator.webdriver;
  const [visible, setVisible] = useState(!lazyEligible);
  const hostRef = useRef(null);
  useEffect(() => {
    if (!lazyEligible || visible) return;
    const el = hostRef.current;
    if (!el || typeof IntersectionObserver === "undefined") { setVisible(true); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((en) => en.isIntersecting)) { setVisible(true); io.disconnect(); }
    }, { rootMargin: "400px" });
    io.observe(el);
    return () => io.disconnect();
  }, [lazyEligible, visible]);
  const [mapName, setMapName] = useState(null);     // active sub-map (USA / ...) for drill levels
  const [shapeMap, setShapeMap] = useState(undefined); // item 45: fixed state/province map (Shape Kind)
  const [worldReady, setWorldReady] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [drill, setDrill] = useState([]);           // [{field, value}]
  const zoomMemo = useRef({});                      // item 7: scroll window per drill level
  const needsExt = EXT_TYPES.includes(type);
  const [extReady, setExtReady] = useState(!needsExt);
  const chartRef = useRef(null);
  const rawLabelRef = useRef({});                   // item 9: pretty date label -> raw queryable value
  const geoRawRef = useRef({});                     // items 11/12: map region name -> raw dataset value
  const rawOf = (name) => {
    if (name == null) return name;
    const k = String(name);
    if (rawLabelRef.current[k] != null) return rawLabelRef.current[k];
    const geoHit = geoRawRef.current[regionMatchKey(k)];
    if (geoHit != null) return geoHit;
    return name;
  };

  // Lazy-load ECharts extensions (3D / wordcloud / liquidfill) only when required.
  useEffect(() => {
    if (!needsExt) { setExtReady(true); return; }
    let active = true;
    setExtReady(false);
    loadExtensions().then(() => { if (active) setExtReady(true); }).catch(() => { if (active) setExtReady(true); });
    return () => { active = false; };
  }, [needsExt, type]);

  // Insight line: computed in the browser, revealed by the lightbulb. The author's choice is
  // saved on the widget so preview, published links, TV mode and PDF exports show it too.
  const authorInsight = !!(config.options || {}).insight;
  const [insightOn, setInsightOn] = useState(authorInsight);
  const [explainOpen, setExplainOpen] = useState(false);
  useEffect(() => { setInsightOn(authorInsight); }, [authorInsight]);
  const toggleInsight = () => {
    const next = !insightOn;
    setInsightOn(next);
    if (onConfigPatch) onConfigPatch(widget.id, { options: { ...(config.options || {}), insight: next } });
  };

  const isGeo = GEO_TYPES.includes(type);
  const canDrill = DRILLDOWN_TYPES.includes(type);
  const drillPath = config.drilldownPath || [];
  const level = drill.length;
  const isPlaceholder = !dataset_id && !["text", "image"].includes(type) && !widget?.federated_spec;

  // Effective grouping dimensions for the current drill level
  // Item 45: "point level" = drilled past the last available shape map (state → city, or country without a
  // state map) → cities are plotted as points on the parent map. Lat/lon fields (config.geo) join the query.
  const mapShape = isGeo ? (config.options?.mapShape || "") : "";
  const useShape = !!(mapShape && mapShape !== "world");
  const geoCfg = config.geo || {};
  const pointLevel = isGeo && level > 0 && (useShape || level >= 2 || !countryMapInfo(String(drill[0]?.value ?? "")));
  const pointDims = pointLevel && geoCfg.lat && geoCfg.lon ? [{ field: geoCfg.lat, date_part: "none" }, { field: geoCfg.lon, date_part: "none" }] : [];
  const effectiveDims = (canDrill && level > 0 && drillPath[level - 1])
    ? [drillPath[level - 1], ...pointDims]
    : (config.dimensions || []);
  const currentLevelField = level === 0 ? (config.dimensions?.[0]?.field) : drillPath[level - 1]?.field;
  const canGoDeeper = canDrill && level < drillPath.length;

  // Item 168: "Highlighted" visual-filter mode — widget filters dim non-matching
  // data instead of removing it (charts only; tables/pivots keep normal filtering).
  const vfHighlight = config.filterMode === "highlight" && HIGHLIGHT_TYPES.includes(type)
    && (config.filters || []).some((f) => f.field && ["eq", "in"].includes(f.op || "eq") && f.value != null && f.value !== "");
  const effConfig = vfHighlight
    ? { ...config, dimensions: effectiveDims, filters: [] }
    : { ...config, dimensions: effectiveDims };
  const activeHighlights = [
    ...(highlightFilters || []),
    ...(vfHighlight ? (config.filters || []).filter((f) => f.field && ["eq", "in"].includes(f.op || "eq") && f.value != null && f.value !== "") : []),
  ];
  const drillFilters = drill.map((d) => ({ field: d.field, op: "eq", value: d.value, ...(d.date_part ? { date_part: d.date_part } : {}) }));
  const allFilters = [...globalFilters, ...drillFilters];

  const filterKey = JSON.stringify(globalFilters);
  // `options.insight` is presentation-only — keep it out of the key so the lightbulb never refetches.
  const configKey = JSON.stringify({ ...config, options: { ...(config.options || {}), insight: undefined } });
  const drillKey = JSON.stringify(drill);
  const paramsKey = JSON.stringify(whatIfParams || {});
  // Cross-dataset widget: any field tagged with another dataset routes the query
  // through the relationship-joined model engine (/api/model-query).
  const crossW = [...(config.dimensions || []), ...(config.measures || []), ...(config.filters || []), ...(globalFilters || [])]
    .some((e) => e && e.dataset_id && e.dataset_id !== dataset_id);
  // Cross-source SQL: any chart (not just Perspective) can render a DuckDB combine result.
  // The combine SQL is fixed, so dashboard filters do not apply to these widgets.
  const fedSpec = type === "perspective" ? null : federatedSpecOf(widget);
  const fedKey = JSON.stringify(fedSpec || "");

  // AI helpers stay out of public/cross-source widgets; the insight line works everywhere.
  const aiOk = !publicToken && !crossW && !!dataset_id;
  const insightShow = INSIGHT_TYPES.includes(type) && !!dataset_id;
  const d0 = effectiveDims[0] || null;
  const isTimeAxis = !!(d0 && ((d0.date_part && d0.date_part !== "none") || /date|month|year|quarter|week/i.test(d0.field || "")));

  // Batch 8: baseline-vs-scenario comparison (baseline = parameter defaults)
  const baselineKey = JSON.stringify(whatIfBaseline || {});
  const compareOn = !!whatIfCompare && WHATIF_COMPARE_TYPES.includes(type)
    && !!whatIfBaseline && !crossW && paramsKey !== baselineKey;

  // Reset drill when the widget config or external filters change
  useEffect(() => { setDrill([]); zoomMemo.current = {}; }, [configKey]);

  // Load base world map for geo widgets (only mark ready AFTER registration)
  const [citiesOk, setCitiesOk] = useState(false);
  useEffect(() => {
    if (!isGeo) return;
    let active = true;
    ensureCityCentroids().then(() => { if (active) setCitiesOk(true); });
    return () => { active = false; };
  }, [isGeo]);
  useEffect(() => {
    if (!isGeo) return;
    if (mapShape.startsWith("custom:")) {
      setMapLoading(true);
      setShapeMap(undefined);
      ensureCustomMap(mapShape.slice(7), publicToken).then((m) => { setShapeMap(m || null); setMapLoading(false); });
    } else if (mapShape && mapShape !== "world") {
      setMapLoading(true);
      setShapeMap(undefined);
      ensureCountryMap(mapShape).then((m) => { setShapeMap(m || null); setMapLoading(false); });
    } else {
      setShapeMap(undefined);
      ensureWorldMap().then(() => setWorldReady(true));
    }
  }, [isGeo, mapShape, publicToken]);

  // For geo drilldown: when drilled into a country, try to load its sub-map
  useEffect(() => {
    if (!isGeo || level === 0) { setMapName(null); return; }
    const country = drill[0]?.value;
    setMapLoading(true);
    ensureCountryMap(country).then((m) => { setMapName(m || null); setMapLoading(false); });
    // keyed by serialized drill path; drill/level identities change every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGeo, drillKey]);

  // The map registered & ready for the current drill level (null => no map yet)
  const activeMap = isGeo
    ? (level === 0 ? (useShape ? (shapeMap || null) : (worldReady ? "world" : null)) : mapName)
    : null;

  const needsData = ["kpi", "gauge", "liquidfill", "bar", "bar_stacked", "line", "area", "combo", "pie", "donut", "scatter", "heatmap", "treemap", "radar", "funnel", "tornado", "sunburst", "sankey", "graph", "candlestick", "wordcloud", "bar_3d", "scatter_3d", "pictorial_bar", "image_scatter", "waterfall", "boxplot", "calendar_heatmap", "table", "pivot", "geo_map", "bubble_map", "point_map", "google_map"].includes(type)
    && !isChartGrid(type, config);   // item 10: each grid instance runs its own query

  useEffect(() => {
    let active = true;
    if (!needsData || isPlaceholder || !visible) return;
    if (fedSpec) {
      setLoading(true); setError(null); setErrorCode(null);
      const payload = {
        ...federatedPayload(fedSpec), limit: 20000,
        // Cross-source widgets slice like any other: matching filters are pushed into their
        // subquery, and {{where:…}} tokens in the combine SQL are resolved server-side.
        filters: [...(config.filters || []), ...(globalFilters || [])].map((f) => ({
          filter_id: f.filter_id, dataset_id: f.dataset_id, field: f.field,
          op: f.op, value: f.value, date_part: f.date_part, fields: f.fields,
        })),
      };
      const req = publicToken
        ? api.post("/public/federated/query", { token: publicToken, ...payload }, { timeout: 90000 })
        : api.post("/federated/query", payload, { timeout: 90000 });
      req.then(({ data: d }) => { if (active) setData({ ...d, engine: d.engine || "duckdb-federated" }); })
        .catch((e) => {
          if (!active || isCancel(e)) return;
          const detail = e?.response?.data?.detail;
          setError(typeof detail === "string" ? detail : detail?.message || "Cross-source query failed");
        })
        .finally(() => { if (active) setLoading(false); });
      return () => { active = false; };
    }
    const serverPivot = type === "pivot" && pivotEngine === "server" && !crossW;
    let effQ = effConfig, nRowDims = 0;
    if (serverPivot) {
      const { rowObjs, colObjs } = splitPivotDimObjects(effConfig);
      effQ = { ...effConfig, dimensions: [...rowObjs, ...colObjs] };
      nRowDims = rowObjs.length;
    }
    const semQuery = buildSemanticQuery(effQ, allFilters);
    // Items 8/9: a time axis is chronological by default (the engine otherwise falls back to measure-desc)
    if (TIME_SORT_TYPES.includes(type) && !semQuery.sort?.length && !effQ.topN?.n) {
      const d0 = (effQ.dimensions || [])[0];
      if (d0 && d0.date_part && d0.date_part !== "none") semQuery.sort = [{ key: dimOut(d0), direction: "asc" }];
    }
    if (whatIfParams && Object.keys(whatIfParams).length) semQuery.params = whatIfParams;
    const crossQ = crossW ? buildModelQuery(effQ, dataset_id, allFilters) : null;
    if (crossW ? (!crossQ.measures.length && !crossQ.dimensions.length)
      : (!semQuery.measures.length && !semQuery.adhoc_measures.length && !semQuery.dimensions.length)) return;
    setLoading(true); setError(null); setErrorCode(null);
    retryCountRef.current = 0;
    // Item 193: shared cache key — dedupes identical queries across widgets; refresh/retry bust it.
    const cacheKey = JSON.stringify({ d: dataset_id, q: crossW ? crossQ : semQuery, x: crossW, p: publicToken || null, sp: serverPivot ? nRowDims : -1, r: refreshToken, rt: retryToken });
    let shared = null;
    const exec = () => {
      shared = fetchShared(cacheKey, (signal) => crossW
        ? (publicToken
          ? api.post("/public/model-query", { token: publicToken, body: crossQ }, { timeout: 60000, signal }).then(({ data: d }) => d)
          : api.post("/model-query", crossQ, { timeout: 60000, signal }).then(({ data: d }) => d))
        : serverPivot
        ? (publicToken
          ? api.post("/public/model/pivot", { token: publicToken, dataset_id, query: semQuery, n_row_dims: nRowDims }, { timeout: 45000, signal }).then(({ data: d }) => d)
          : api.post(`/datasets/${dataset_id}/model/pivot`, { query: semQuery, n_row_dims: nRowDims }, { timeout: 45000, signal }).then(({ data: d }) => d))
        : runQuery({ datasetId: dataset_id, semQuery, publicToken, signal }));
      return shared.promise;
    };
    const t0 = performance.now();
    const start = () => {
      runWithRetry(exec, (n) => { retryCountRef.current = n; })
        .then((d) => {
          if (!active) return;
          setData(d);
          logWidgetPerf({ widget, dashboardId, apiMs: Math.round(performance.now() - t0), rows: d?.rows?.length ?? 0 });
        })
        .catch((e) => {
          if (!active || isCancel(e)) return;
          const detail = e?.response?.data?.detail;
          const msg = e?.code === "ECONNABORTED" ? "Query timed out — try narrowing filters"
            : (typeof detail === "string" ? detail : detail?.message || "Failed to load data");
          setError(msg);
          const code = typeof detail === "object" && detail ? detail.code || null : null;
          setErrorCode(code);
          if (code === "dataset_access_denied") return; // expected under the strict dataset rule — not a client error
          reportClientError({
            stage: "widget_query", message: String(msg), dashboard_id: dashboardId,
            widget_id: widget.id, widget_type: type, retry_count: retryCountRef.current,
          });
        })
        .finally(() => { if (active) setLoading(false); });
    };
    // Debounce refetches while data is on screen (rapid filter changes); first load fires instantly.
    const timer = data ? setTimeout(start, 180) : (start(), null);
    return () => { active = false; if (timer) clearTimeout(timer); shared?.release(); };
    // data fetch is keyed by the serialized config/filter/drill keys above
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataset_id, configKey, filterKey, drillKey, type, needsData, isPlaceholder, publicToken, refreshToken, pivotEngine, paramsKey, visible, retryToken, fedKey]);

  // Batch 8: fetch baseline (parameter defaults) data for the compare-vs-baseline toggle
  const [baseData, setBaseData] = useState(null);
  useEffect(() => {
    let active = true;
    if (!compareOn || !needsData || isPlaceholder || !visible) { setBaseData(null); return; }
    const semQuery = buildSemanticQuery(effConfig, allFilters);
    semQuery.params = whatIfBaseline;
    if (!semQuery.measures.length && !semQuery.adhoc_measures.length && !semQuery.dimensions.length) return;
    runQuery({ datasetId: dataset_id, semQuery, publicToken })
      .then((d) => { if (active) setBaseData(d); })
      .catch(() => { if (active) setBaseData(null); });
    return () => { active = false; };
    // keyed by the same serialized keys as the main fetch + baselineKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareOn, dataset_id, configKey, filterKey, drillKey, type, needsData, isPlaceholder, publicToken, refreshToken, baselineKey, visible]);

  // Forecast (Item 161): only for single time-dimension line/area charts.
  const fcEnabled = !!(config.forecast && config.forecast.enabled) && ["line", "area"].includes(type)
    && !publicToken && !crossW && (config.dimensions || []).length === 1
    && !!config.dimensions[0]?.date_part && config.dimensions[0].date_part !== "none";
  const [forecast, setForecast] = useState(null);
  useEffect(() => {
    let active = true;
    if (!fcEnabled || !dataset_id) { setForecast(null); return; }
    const semQuery = buildSemanticQuery(config, allFilters);
    if (whatIfParams && Object.keys(whatIfParams).length) semQuery.params = whatIfParams;
    api.post(`/datasets/${dataset_id}/model/forecast`, { query: semQuery, periods: Number(config.forecast?.periods) || 6 })
      .then(({ data: d }) => { if (active) setForecast(d); })
      .catch(() => { if (active) setForecast(null); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fcEnabled, dataset_id, configKey, filterKey, refreshToken, paramsKey]);

  const handleDrillClick = (params) => {
    if (!canGoDeeper) return;
    const name = params?.name;
    if (!name || !currentLevelField) return;
    const dp = level === 0 ? config.dimensions?.[0]?.date_part : drillPath[level - 1]?.date_part;
    // a deeper level starts at the first category; the current window stays remembered for drill-up
    delete zoomMemo.current[level + 1];
    const raw = rawOf(name);
    setDrill((d) => [...d, { field: currentLevelField, value: raw, ...(String(raw) !== String(name) ? { label: String(name) } : {}), ...(dp && dp !== "none" ? { date_part: dp } : {}) }]);
  };
  const drillUp = () => setDrill((d) => d.slice(0, -1));
  const drillReset = () => setDrill([]);

  // Embedded Action Framework: chart click -> open URL / webhook / drill-through dashboard
  const action = config.action || {};
  const hasAction = !publicToken && action.type && action.type !== "none";
  // Cross-filtering: click a data point -> filter other widgets on the same dataset
  const cfDim = (canDrill && level > 0 && drillPath[level - 1]) ? drillPath[level - 1] : (config.dimensions || [])[0];
  const canCrossFilter = !!onCrossFilter && config.cross_filter !== false && !!cfDim
    && (!cfDim.date_part || cfDim.date_part === "none");
  const emitCrossFilter = (params) => {
    // Item 51: radar polygons are measures, not categories — only indicator (axis-name) clicks filter
    if (type === "radar" && params?.componentType !== "radar") return;
    const v = params?.name;
    if (v == null || v === "") return;
    try { chartRef.current?.getEchartsInstance()?.dispatchAction({ type: "hideTip" }); } catch (e) { console.debug("hideTip skipped", e); }
    setTimeout(() => onCrossFilter({ key: cfDim.key || null, field: cfDim.field || null }, rawOf(v)), 0);
  };
  const runWidgetAction = (params) => {
    const ctx = { field: currentLevelField || "", value: params?.name ?? "", series: params?.seriesName ?? "", data: params?.value ?? "" };
    const fill = (s) => String(s || "").replace(/\{(value|field|series|data)\}/g, (_, k) => encodeURIComponent(ctx[k]));
    if (action.type === "url" && action.url) {
      window.open(fill(action.url), "_blank", "noopener");
    } else if (action.type === "webhook" && action.webhook_url) {
      toast.promise(api.post("/actions/webhook", { url: action.webhook_url, payload: { widget: widget.title || widget.type, ...ctx } }),
        { loading: "Calling webhook…", success: "Webhook triggered", error: (e) => e?.response?.data?.detail || "Webhook failed" });
    } else if (action.type === "drill" && action.target_dashboard_id) {
      window.location.assign(`/builder/${action.target_dashboard_id}`);
    }
  };

  if (type === "text") {
    if (config.rich_enabled) {
      return <RichTextWidget widget={widget} editable={!!onConfigPatch} onConfigPatch={onConfigPatch} params={whatIfParams} />;
    }
    return <DynamicTextView config={config} datasetId={dataset_id} globalFilters={globalFilters} publicToken={publicToken} render={renderMarkdownLite} params={whatIfParams} />;
  }

  if (type === "image") return <ImageWidget config={config} globalFilters={globalFilters} publicToken={publicToken} />;

  if (type === "multi_filter") {
    return <MultiFilterWidget widget={widget} publicToken={publicToken} values={multiFilterValues}
      onChange={onSlicerChange} globalFilters={globalFilters} />;
  }

  if (["combo_box", "list_box", "date_picker"].includes(type)) {
    if (!config.dimensions?.[0]?.field) {
      return <Center><div data-testid="widget-unconfigured" className="text-center text-[color:var(--ink-3)] text-sm px-4"><Inbox className="w-6 h-6 mx-auto mb-2 opacity-60" /><div className="font-medium text-[color:var(--ink-2)]">Pick a filter field</div><div className="text-xs mt-1">Choose the field in the properties panel.</div></div></Center>;
    }
    const fwProps = { widget, publicToken, value: filterValue, onChange: onFilterChange || (() => {}), globalFilters };
    if (type === "combo_box") return <ComboBoxFilterWidget {...fwProps} />;
    if (type === "list_box") return <ListBoxFilterWidget {...fwProps} />;
    return <DateFilterWidget {...fwProps} />;
  }

  if (type === "data_grid") {
    return (
      <React.Suspense fallback={<Center><Loader2 className="w-5 h-5 animate-spin text-[color:var(--brand-ocean)]" /></Center>}>
        <DataGridWidget widget={widget} publicToken={publicToken}
          filters={[...(config.filters || []), ...globalFilters].filter((f) => f && f.field)} />
      </React.Suspense>
    );
  }

  // Lazy init: mount a lightweight skeleton host until the widget scrolls near the viewport.
  if (needsData && !isPlaceholder && !visible) {
    return <div ref={hostRef} data-testid="widget-lazy-host" className="h-full w-full opacity-60"><WidgetSkeleton type={type} /></div>;
  }

  if (type === "slicer") {
    if (!config.dimensions?.[0]?.field) {
      return <Center><div data-testid="widget-unconfigured" className="text-center text-[color:var(--ink-3)] text-sm px-4"><Inbox className="w-6 h-6 mx-auto mb-2 opacity-60" /><div className="font-medium text-[color:var(--ink-2)]">Pick a filter field</div><div className="text-xs mt-1">Choose the field in the properties panel.</div></div></Center>;
    }
    return <SlicerFilterWidget widget={widget} publicToken={publicToken} value={slicerValue} onChange={onSlicerChange} globalFilters={globalFilters} />;
  }

  // Scenario Compare (Batch 8 follow-up): measures side-by-side per saved scenario.
  if (type === "scenario_grid") {
    return (
      <div className="h-full w-full relative">
        <ScenarioGridWidget widget={widget} publicToken={publicToken} globalFilters={allFilters}
          whatIfDefs={whatIfDefs} scenarios={whatIfScenarios} currentParams={whatIfParams} />
      </div>
    );
  }

  // Perspective widget renders its own data (regular or federated) and its own UI.
  if (type === "perspective") {
    const hasFederated = widget.federated_spec?.subqueries?.length
      && widget.federated_spec?.combine_sql;
    if (!widget.dataset_id && !hasFederated) {
      return <Center><div className="text-center text-[color:var(--ink-3)] text-sm"><Inbox className="w-6 h-6 mx-auto mb-2 opacity-60" />Pick a dataset or configure a Federated (cross-source) query</div></Center>;
    }
    return <PerspectiveWidget widget={widget} onConfigChange={publicToken ? null : onConfigChange} />;
  }

  if (isPlaceholder) return <Center><div className="text-center text-[color:var(--ink-3)] text-sm"><Inbox className="w-6 h-6 mx-auto mb-2 opacity-60" />Configure this widget</div></Center>;
  if (type === "point_map" && (!config.geo?.lat || !config.geo?.lon)) {
    return (
      <Center>
        <div data-testid="pointmap-unconfigured" className="text-center text-[color:var(--ink-3)] text-sm px-4">
          <Inbox className="w-6 h-6 mx-auto mb-2 opacity-60" />
          <div className="font-medium text-[color:var(--ink-2)]">Map your coordinates</div>
          <div className="text-xs mt-1">Pick latitude & longitude fields in the Data panel.</div>
        </div>
      </Center>
    );
  }
  if (fedSpec && !(config.measures || []).length && !(config.dimensions || []).length) {
    return (
      <Center>
        <div data-testid="widget-fed-unmapped" className="text-center text-[color:var(--ink-3)] text-sm px-4">
          <Inbox className="w-6 h-6 mx-auto mb-2 opacity-60" />
          <div className="font-medium text-[color:var(--ink-2)]">Cross-source SQL is ready</div>
          <div className="text-xs mt-1">Open Data → Cross-source SQL and click “Use these columns” to plot it.</div>
        </div>
      </Center>
    );
  }
  if (needsData && !(config.measures || []).length && !(config.dimensions || []).length && !config.boxField) {
    return (
      <Center>
        <div data-testid="widget-unconfigured" className="text-center text-[color:var(--ink-3)] text-sm px-4">
          <Inbox className="w-6 h-6 mx-auto mb-2 opacity-60" />
          <div className="font-medium text-[color:var(--ink-2)]">Add data to this visual</div>
          <div className="text-xs mt-1">Pick dimensions and measures in the properties panel on the right.</div>
        </div>
      </Center>
    );
  }
  if ((loading && !data) || (isGeo && mapLoading)) return <WidgetSkeleton type={type} />;
  // Item 10: Pie/Doughnut grid — each cell is an independent chart with its own query.
  if (isChartGrid(type, config)) {
    return (
      <div className="h-full w-full relative">
        <ChartGridView widget={widget} type={type} filters={allFilters} publicToken={publicToken} refreshToken={refreshToken} />
      </div>
    );
  }
  if (errorCode === "dataset_access_denied") return (
    <Center>
      <div className="text-center text-[color:var(--ink-3)] text-xs px-3" data-testid="widget-no-dataset-access" data-error-code="dataset_access_denied">
        <Lock className="w-5 h-5 mx-auto mb-1 opacity-70" />
        <div className="font-medium text-[color:var(--ink-2)]">No access to this data source</div>
        <div className="mt-0.5">Ask the dataset owner to share it with you.</div>
      </div>
    </Center>
  );
  if (error) return (
    <Center>
      <div className="text-center text-[color:var(--danger)] text-xs px-3" data-testid="widget-error" data-error-code={errorCode || undefined}>
        <AlertTriangle className="w-5 h-5 mx-auto mb-1" />{error}
        {(errorCode === "relationship_required" || errorCode === "relationship_broken") && !publicToken && dataset_id && (
          <div className="mt-1.5">
            <a data-testid="widget-error-open-model" href={`/model/${dataset_id}?tab=joins`} target="_blank" rel="noreferrer"
              className="text-[11px] underline text-[color:var(--brand-ocean)]">{errorCode === "relationship_broken" ? "Fix the relationship in Model view" : "Define the relationship in Model view"}</a>
          </div>
        )}
        <div className="mt-2">
          <button data-testid="widget-query-retry" onClick={() => setRetryToken((n) => n + 1)}
            className="text-[11px] px-2.5 py-1 rounded-md border border-[color:var(--glass-border)] text-[color:var(--brand-ocean)] hover:bg-[color:var(--surface-2)]">
            {t("retry")}
          </button>
        </div>
      </div>
    </Center>
  );
  if (!data || !data.rows || !data.rows.length) {
    return (
      <div className="h-full w-full flex flex-col">
        {drill.length > 0 && <DrillBar drill={drill} onUp={drillUp} onReset={drillReset} />}
        <Center><div className="text-center text-[color:var(--ink-3)] text-xs"><Inbox className="w-5 h-5 mx-auto mb-1 opacity-60" />{t("no_data")}</div></Center>
      </div>
    );
  }

  if (needsExt && !extReady) return <Center><Loader2 className="w-5 h-5 animate-spin text-[color:var(--brand-ocean)]" /></Center>;

  const dataR = applySortEngine(withFormatOverrides(applyQuickCalcs(expandTableInstances(data, effConfig), effConfig), effConfig), effConfig);

  if (type === "kpi") return <div className="h-full w-full relative"><KpiView data={dataR} config={config} datasetId={dataset_id} publicToken={publicToken} filters={allFilters} baselineData={compareOn && baseData ? withFormatOverrides(applyQuickCalcs(baseData, effConfig), effConfig) : null} /><RefreshingBadge show={loading} /><EngineBadge data={data} /><ExplainButton widget={widget} filters={allFilters} show={!publicToken && !crossW} /></div>;
  if (type === "point_map") {
    return (
      <div className="h-full w-full relative">
        <React.Suspense fallback={<WidgetSkeleton type="point_map" />}>
          <PointMap data={dataR} config={config}
            onPointFilter={onCrossFilter && config.cross_filter !== false ? (field, value) => onCrossFilter({ key: null, field }, value) : null} />
        </React.Suspense>
        <RefreshingBadge show={loading} />
        <EngineBadge data={data} />
      </div>
    );
  }
  if (type === "google_map") {
    return (
      <div className="h-full w-full relative">
        <React.Suspense fallback={<WidgetSkeleton type="point_map" />}>
          <GoogleMapWidget data={dataR} config={config} publicToken={publicToken}
            onPointFilter={onCrossFilter && config.cross_filter !== false ? (field, value) => onCrossFilter({ key: null, field }, value) : null} />
        </React.Suspense>
        <RefreshingBadge show={loading} />
        <EngineBadge data={data} />
      </div>
    );
  }
  // Item 13: cross-dashboard / URL drillthrough shared by charts, tables and pivots
  const dtCfg = normalizeDrillthrough(config.drillthrough);
  const dtActive = dtCfg.enabled && dtCfg.targets.length > 0;
  const openDrill = (clicked, evt) => {
    const ctxFor = (t) => buildDrillContext({ dims: [], carry: t.carry, widgetFilters: [...(config.filters || []), ...drillFilters], globalFilters: globalFilters.filter((f) => f.field) })
      .filter((f) => t.carry?.clicked === false || !clicked.some((c) => c.field === f.field))
      .concat(t.carry?.clicked === false ? [] : clicked);
    const runTarget = async (t) => {
      const res = await navigateDrill(t, ctxFor(t), {
        from: dashboardName || widget.title || "Source dashboard", sourceId: dashboardId || "", publicToken: publicToken || "",
        trail: drillTrail || null,
      });
      if (!res.ok) toast.error(res.error);
      else {
        if (res.modal) setDtModal(res.modal);
        if (res.stripped?.length) toast.info(`Some context wasn't carried (restricted fields): ${res.stripped.join(", ")}`);
      }
    };
    const availability = (t) => targetAvailability(t, ctxFor(t));
    if (dtCfg.targets.length === 1) {
      const av = availability(dtCfg.targets[0]);
      if (!av.ok) { toast.info(`Drillthrough unavailable — ${av.reason}`); return; }
      runTarget(dtCfg.targets[0]);
      return;
    }
    setDtMenu({ x: evt?.clientX ?? 0, y: evt?.clientY ?? 0, run: runTarget, targets: dtCfg.targets, availability });
  };
  const rowDrill = (row, e) => {
    const clicked = (config.dimensions || []).filter((d) => d?.field && (!d.date_part || d.date_part === "none") && row[d.field] != null && row[d.field] !== "")
      .map((d) => ({ field: d.field, op: "eq", value: row[d.field] }));
    openDrill(clicked, e);
  };
  if (type === "table") return <div className="h-full w-full relative"><TableView data={dataR} config={config} onConfigChange={onConfigChange}
    onRowDrill={dtActive ? rowDrill : null} drillTrigger={dtCfg.trigger} />{dtMenu && <DrillTargetMenu menu={dtMenu} onClose={() => setDtMenu(null)} />}{dtModal && <DrillthroughModal modal={dtModal} onClose={() => setDtModal(null)} />}<RefreshingBadge show={loading} /><EngineBadge data={data} /><ExplainButton widget={widget} filters={allFilters} show={!publicToken && !crossW} /></div>;
  if (type === "pivot") return <div className="h-full w-full relative"><PivotTable data={dataR} config={config} onConfigChange={onConfigChange} datasetId={dataset_id} publicToken={publicToken}
    onCellDrill={onDrillthrough ? (fs) => onDrillthrough([...(config.filters || []), ...globalFilters.filter((f) => f.field), ...fs]) : null}
    onCellDrillthrough={dtActive ? (fs, e) => openDrill(fs, e) : null} drillTrigger={dtCfg.trigger} />{dtMenu && <DrillTargetMenu menu={dtMenu} onClose={() => setDtMenu(null)} />}{dtModal && <DrillthroughModal modal={dtModal} onClose={() => setDtModal(null)} />}<RefreshingBadge show={loading} /><EngineBadge data={data} /><ExplainButton widget={widget} filters={allFilters} show={!publicToken && !crossW} /></div>;

  const styleOpts = { ...(config.options || {}) };
  // Per-visual field renames (config .label) surface in chart legends via seriesLabels
  // Items 32/34: model display names (query meta) are the semantic default under the widget renames
  const renameLabels = labelsFromMeta(data);
  (config.measures || []).forEach((m) => {
    if (m && m.label) renameLabels[m.key || m.alias || `${m.agg || "sum"}_${m.field}`] = m.label;
  });
  (config.dimensions || []).forEach((d, i) => {
    if (d && d.label) renameLabels[dimKeyAt(config.dimensions, i)] = d.label;
  });
  if (Object.keys(renameLabels).length) styleOpts.seriesLabels = { ...renameLabels, ...(styleOpts.seriesLabels || {}) };
  // Band drill insights: clicking a banded/binned group opens the rows behind it
  const bandDim0 = (config.dimensions || []).find((d) => d && (d.band || d.bin));
  const canBandDrill = !!onBandDrill && !!bandDim0 && !publicToken;
  const emitBandDrill = (params) => {
    const bandIsFirst = (config.dimensions || [])[0] === bandDim0;
    const v = bandIsFirst ? params?.name : (params?.seriesName ?? params?.name);
    if (v == null || v === "") return;
    try { chartRef.current?.getEchartsInstance()?.dispatchAction({ type: "hideTip" }); } catch (e) { console.debug("hideTip skipped", e); }
    setTimeout(() => onBandDrill(widget, bandDim0, String(v), allFilters), 0);
  };
  const baseEvents = canGoDeeper ? { click: handleDrillClick }
    : hasAction ? { click: runWidgetAction }
      : canBandDrill ? { click: emitBandDrill }
        : canCrossFilter ? { click: emitCrossFilter } : {};
  const onCtxMenu = (params) => {
    try { params?.event?.event?.preventDefault?.(); } catch { /* fall back to native menu */ }
    const extra = [];
    const v = params?.name;
    if (v != null && v !== "" && cfDim?.field && (!cfDim.date_part || cfDim.date_part === "none")) {
      extra.push({ field: cfDim.field, op: "eq", value: v });
    }
    onDrillthrough([...(config.filters || []), ...globalFilters.filter((f) => f.field), ...drillFilters, ...extra]);
  };
  const drillEvents = (() => {
    const ev = { ...baseEvents };
    // Item 7: remember the scroll window per drill level so drilling up restores the viewport
    ev.datazoom = (e) => {
      const z = Array.isArray(e?.batch) ? e.batch[0] : e;
      if (z && z.start != null && z.end != null) zoomMemo.current[level] = { start: z.start, end: z.end };
    };
    if (onDrillthrough) ev.contextmenu = onCtxMenu;
    // Item 13: drillthrough to other dashboards / URLs, carrying the clicked context (config.drillthrough)
    if (dtActive) {
      const fire = (params) => {
        try { params?.event?.event?.preventDefault?.(); } catch { /* native menu fallback */ }
        const dims = [cfDim, ...(config.dimensions || []).slice(1)].filter(Boolean);
        const clicked = buildDrillContext({ params: { ...params, name: rawOf(params?.name) }, dims, carry: { clicked: true, widget_filters: false, global_filters: false } });
        openDrill(clicked, params?.event?.event);
      };
      if (dtCfg.trigger === "click") ev.click = fire;
      else if (dtCfg.trigger === "double_click") ev.dblclick = fire;
      else ev.contextmenu = fire;
    }
    return ev;
  })();

  // Geographic maps (with geographical drilldown)
  if (isGeo) {
    const baseMap = useShape ? shapeMap : (worldReady ? "world" : null);
    const parentMap = pointLevel ? (level >= 2 ? (mapName || baseMap) : baseMap) : null;
    const hasLatLon = !!(geoCfg.lat && geoCfg.lon);
    // Waiting for the required map to register (a failed shape-map load falls through to bars)
    if (mapLoading || (level === 0 && !activeMap && !(useShape && shapeMap === null)) || (pointLevel && !citiesOk && !hasLatLon)) {
      return <Center><Loader2 className="w-5 h-5 animate-spin text-[color:var(--brand-ocean)]" /></Center>;
    }
    const gspec = { x_axis: dimOut(effectiveDims[0]), y_axis: (effConfig.measures || []).map(measOut), series: !pointLevel && effectiveDims[1] ? dimOut(effectiveDims[1]) : null };
    // Items 11/12: clicking a region drills on the dataset's own spelling, not the map's
    geoRawRef.current = Object.fromEntries(((data && data.rows) || [])
      .map((r) => r[gspec.x_axis])
      .filter((v) => v != null && v !== "")
      .map((v) => [regionMatchKey(v), v]));
    const zoomGeoBy = (goption, f) => {
      const inst = chartRef.current?.getEchartsInstance();
      if (!inst) return;
      if (f === 0) { inst.setOption(goption, true); return; }
      const o = inst.getOption();
      const usesGeo = o.geo && o.geo.length;
      const cur = usesGeo ? (o.geo[0].zoom || 1) : ((o.series?.[0] || {}).zoom || 1);
      const z = Math.max(1, Math.min(12, cur * f));
      if (usesGeo) inst.setOption({ geo: { zoom: z } });
      else inst.setOption({ series: [{ zoom: z }] });
    };
    const renderMap = (goption, note) => (
      <div className="h-full w-full flex flex-col relative">
        {drill.length > 0 && <DrillBar drill={drill} onUp={drillUp} onReset={drillReset} note={note} />}
        {drill.length === 0 && note && <div data-testid="map-unmatched-note" className="px-1 pb-1 text-[10px] italic text-[color:var(--ink-3)] truncate">{note}</div>}
        {styleOpts.mapZoomControls !== false && (
          <div className="absolute bottom-6 left-1 z-10 flex flex-col gap-0.5" style={insightOn ? { bottom: "3.5rem" } : undefined}>
            {[["map-zoom-in", "+", 1.4], ["map-zoom-out", "−", 1 / 1.4], ["map-zoom-reset", "⌂", 0]].map(([tid, txt, f]) => (
              <button key={tid} data-testid={tid} onClick={() => zoomGeoBy(goption, f)}
                className="w-6 h-6 rounded-md bg-[color:var(--surface)] border border-[color:var(--glass-border)] text-[color:var(--ink-2)] text-xs leading-none shadow-sm hover:bg-[color:var(--surface-2)]">
                {txt}
              </button>
            ))}
          </div>
        )}
        <div className="flex-1 min-h-0">
          <ReactECharts ref={chartRef} option={goption} style={{ height: "100%", width: "100%", minHeight: 140 }} opts={{ renderer: "canvas" }} notMerge onEvents={drillEvents} />
        </div>
        {/* Items 11/12: regions are reachable by keyboard too — Enter drills into the focused region */}
        <ChartKeyboardNav widget={widget} data={dataR} type={type} chartRef={chartRef}
          onActivate={(params) => (drillEvents.click || drillEvents.dblclick || drillEvents.contextmenu)?.(params)} />
        {insightOn && <InsightStrip lines={buildInsights({ type, data: dataR, spec: gspec, options: styleOpts })} onAskAi={aiOk ? () => setExplainOpen(true) : null} />}
        {insightShow && <InsightToggle on={insightOn} onToggle={toggleInsight} right="0.25rem" />}
        <ExplainButton widget={widget} filters={allFilters} show={aiOk} open={explainOpen} setOpen={setExplainOpen} hideBtn />
      </div>
    );
    // Item 45: city / point level — dataset lat/lon first, built-in city centroids as fallback
    const resolvePoints = (names) => {
      const nameKey = gspec.x_axis;
      const tmapG = styleOpts.territoryMap || {};
      const byName = new Map();
      ((data && data.rows) || []).forEach((r) => {
        const raw = String(r[nameKey]);
        const name = tmapG[raw] || raw;
        if (names && !names.has(name)) return;
        const acc = byName.get(name) || { row: { [nameKey]: name }, lat: null, lon: null };
        gspec.y_axis.forEach((k) => { acc.row[k] = (acc.row[k] || 0) + (Number(r[k]) || 0); });
        if (hasLatLon && acc.lat == null) {
          const la = Number(r[geoCfg.lat]), lo = Number(r[geoCfg.lon]);
          if (Number.isFinite(la) && Number.isFinite(lo) && Math.abs(la) <= 90 && Math.abs(lo) <= 180) { acc.lat = la; acc.lon = lo; }
        }
        byName.set(name, acc);
      });
      const unmatched = [];
      const points = [...byName.entries()].map(([name, acc]) => {
        const c = acc.lat != null ? [acc.lon, acc.lat] : lookupCity(name);
        if (!c) { unmatched.push(name); return null; }
        return { name, value: [c[0], c[1], acc.row[gspec.y_axis[0]] || 0], row: acc.row };
      }).filter(Boolean);
      return { points, unmatched };
    };
    if (pointLevel && parentMap) {
      const { points, unmatched } = resolvePoints(null);
      if (points.length) {
        const goption = buildBubbleMap(dataR, gspec, {}, styleOpts, parentMap, { points, view: fitGeoView(points, getCentroids(parentMap)) });
        return renderMap(goption, unmatched.length ? `${unmatched.length} without coordinates` : null);
      }
    }
    if (activeMap && !pointLevel) {
      const regionNames = getRegionNames(activeMap);
      const goption = type === "geo_map"
        ? buildChoropleth(dataR, gspec, styleOpts, activeMap, regionNames.length ? regionNames : null)
        : buildBubbleMap(dataR, gspec, getCentroids(activeMap), styleOpts, activeMap, { lookup: lookupCity });
      let un = goption.__unmatched || [];
      // Choropleth: values that are cities (not regions) become a point layer on the same map
      if (type === "geo_map" && un.length && styleOpts.mapCityLayer !== false) {
        const { points } = resolvePoints(new Set(un));
        if (points.length) {
          const bm = buildBubbleMap(dataR, gspec, {}, styleOpts, activeMap, { points });
          goption.geo = bm.geo;
          goption.series = [{ ...goption.series[0], geoIndex: 0 }, ...bm.series.map((s) => ({ ...s, z: 5 }))];
          if (goption.visualMap) goption.visualMap.seriesIndex = [0];
          const matched = new Set(points.map((p) => p.name));
          un = un.filter((n) => !matched.has(n));
        }
      }
      return renderMap(goption, un.length && styleOpts.mapUnmatchedNote !== false ? `${un.length} unmatched: ${un.slice(0, 3).join(", ")}${un.length > 3 ? "…" : ""}` : null);
    }
    // Fallback: no map / no coordinates for this drill level -> bar chart
    const bspec = { chart_type: "bar", x_axis: dimOut(effectiveDims[0]), y_axis: (effConfig.measures || []).map(measOut) };
    const bopt = buildOption("bar", dataR, bspec, styleOpts);
    return (
      <div className="h-full w-full flex flex-col">
        {drill.length > 0 && <DrillBar drill={drill} onUp={drillUp} onReset={drillReset} note={pointLevel ? "shown as chart (no coordinates found)" : "shown as chart (no sub-map)"} />}
        <div className="flex-1 min-h-0">
          <ReactECharts option={bopt} style={{ height: "100%", width: "100%" }} opts={{ renderer: "svg" }} notMerge onEvents={drillEvents} />
        </div>
      </div>
    );
  }

  const spec = { chart_type: type, x_axis: dimKeyAt(effectiveDims, 0), y_axis: (effConfig.measures || []).map(measOut), series: effectiveDims[1] ? dimKeyAt(effectiveDims, 1) : null, dims: dimOutNames(effectiveDims).filter(Boolean) };
  // Items 179/182: friendly date labels + client-side chart sorting
  // Item 29: explicit sort levels win over the chart-specific legacy sort option
  const engineSorted = sortLevels(effConfig).length > 0;
  const dataC = applyDateLabels(engineSorted ? dataR : applyChartSort(dataR, spec, styleOpts), spec, styleOpts);
  rawLabelRef.current = dataC.rawByLabel || {};
  const option = buildOption(type, dataC, spec, styleOpts);
  // Item 180 v2: value-range colors across chart families (bar/line/scatter/pie/funnel/treemap/tornado)
  if (["bar", "bar_stacked", "line", "area", "combo", "scatter"].includes(type)) {
    applyRefLines(option, styleOpts, styleOpts.orientation === "horizontal" && ["bar", "bar_stacked"].includes(type));
    applyTrendline(option, styleOpts);
    if (styleOpts.anomalies) applyAnomalyMarkers(option, Number(styleOpts.anomalyThreshold) || 2.5);
    if (["line", "area", "combo"].includes(type)) applySeriesStyles(option, styleOpts);
  }
  // Item 86: advanced color modes (range | rule | category | low-mid-high) applied per datum
  const ADV_TYPES = ["bar", "bar_stacked", "line", "area", "combo", "scatter", "bubble", "pie", "donut", "semi_donut", "funnel", "treemap", "tornado", "radar", "gauge", "heatmap"];
  const advMode = advColorMode(styleOpts);
  if (advMode !== "default" && ADV_TYPES.includes(type) && Array.isArray(option.series)) {
    let mm = {};
    if (advMode === "lmh") {
      const all = [];
      option.series.forEach((s) => Array.isArray(s.data) && s.data.forEach((d) => {
        const raw = (d && typeof d === "object" && !Array.isArray(d)) ? d.value : d;
        let v = raw;
        if (Array.isArray(raw)) {
          if (s.type === "heatmap") v = raw[2];
          else if (s.type === "radar") { const ns = raw.map(Number).filter(Number.isFinite); v = ns.length ? ns.reduce((a, b) => a + b, 0) / ns.length : null; }
          else v = raw[1];
        }
        if (Number.isFinite(Number(v))) all.push(Number(v));
      }));
      mm = { min: Math.min(...all), max: Math.max(...all) };
    }
    const resolver = advStyleResolver(styleOpts, mm);
    if (resolver) {
      const ax = Array.isArray(option.xAxis) ? option.xAxis[0] : option.xAxis;
      const yax = Array.isArray(option.yAxis) ? option.yAxis[0] : option.yAxis;
      const cats = (ax && ax.type === "category" && ax.data) || (yax && yax.type === "category" && yax.data) || [];
      option.series = option.series.map((s) => {
        if (!Array.isArray(s.data)) return s;
        if (["bar", "line", "scatter", "effectScatter"].includes(s.type)) {
          return {
            ...s,
            data: s.data.map((d, di) => {
              const raw = (d && typeof d === "object" && !Array.isArray(d)) ? d.value : d;
              const v = Array.isArray(raw) ? raw[1] : raw;
              const st = resolver(v, cats[di]);
              if (!st) return d;
              const base = (d && typeof d === "object" && !Array.isArray(d)) ? d : { value: d };
              return { ...base, itemStyle: { ...(base.itemStyle || {}), ...st } };
            }),
          };
        }
        if (["pie", "funnel", "treemap"].includes(s.type)) {
          return {
            ...s,
            data: s.data.map((d) => {
              const v = d && typeof d === "object" ? d.value : d;
              const st = resolver(v, d && typeof d === "object" ? d.name : null);
              if (!st) return d;
              return { ...d, itemStyle: { ...((d && d.itemStyle) || {}), ...st } };
            }),
          };
        }
        if (s.type === "heatmap") {
          return {
            ...s,
            data: s.data.map((d) => {
              const raw = (d && typeof d === "object" && !Array.isArray(d)) ? d.value : d;
              if (!Array.isArray(raw)) return d;
              const st = resolver(raw[2], cats[raw[0]]);
              if (!st) return d;
              const base = (d && typeof d === "object" && !Array.isArray(d)) ? d : { value: d };
              return { ...base, itemStyle: { ...(base.itemStyle || {}), ...st } };
            }),
          };
        }
        if (s.type === "radar") {
          return {
            ...s,
            data: s.data.map((d) => {
              if (!d || typeof d !== "object" || !Array.isArray(d.value)) return d;
              const ns = d.value.map(Number).filter(Number.isFinite);
              const avg = ns.length ? ns.reduce((a, b) => a + b, 0) / ns.length : null;
              const st = resolver(avg, d.name)
                || resolver(avg, String(d.name || "").replace(/^(sum|avg|min|max|count_distinct|count|median)_/, ""));
              if (!st) return d;
              return { ...d, itemStyle: { ...(d.itemStyle || {}), ...st }, lineStyle: { ...(d.lineStyle || {}), ...st } };
            }),
          };
        }
        return s;
      });
    }
    // Range legend integration (auto keeps series legend; 'ranges' swaps it; 'both' shows both)
    const ranges = advMode === "range" ? activeRanges(styleOpts) : [];
    const lm = styleOpts.rangeLegend || "auto";
    const cartesian = ["bar", "bar_stacked", "line", "area", "combo", "scatter", "bubble", "tornado"].includes(type);
    if (ranges.length && (lm === "ranges" || lm === "both") && cartesian) {
      option.series = [...option.series, ...rangeLegendSeries(ranges)];
      option.legend = { ...(option.legend || {}), show: true };
      if (lm === "ranges") option.legend.data = ranges.map((r) => r.label || `${r.start}–${r.end}`);
    }
  }
  if (fcEnabled && forecast && drill.length === 0) applyForecastToOption(option, forecast);
  // Batch 8: overlay baseline (defaults) series alongside the scenario for comparison
  if (compareOn && baseData && baseData.rows && baseData.rows.length) {
    const baseSorted = applySortEngine(withFormatOverrides(applyQuickCalcs(baseData, effConfig), effConfig), effConfig);
    const baseC = applyDateLabels(engineSorted ? baseSorted : applyChartSort(baseSorted, spec, styleOpts), spec, styleOpts);
    const baseOpt = buildOption(type, baseC, spec, styleOpts);
    const extra = (baseOpt.series || []).filter((s) => Array.isArray(s.data)).map((s) => ({
      ...s,
      name: `${s.name} (baseline)`,
      stack: s.stack ? `${s.stack}__baseline` : undefined,
      silent: true,
      z: 0,
      emphasis: { disabled: true },
      label: { show: false },
      itemStyle: { ...(s.itemStyle || {}), opacity: 0.3 },
      lineStyle: { ...(s.lineStyle || {}), type: "dashed", width: 1.5, opacity: 0.55 },
      areaStyle: s.areaStyle ? { ...s.areaStyle, opacity: 0.06 } : undefined,
    }));
    if (extra.length) {
      option.series = [...(option.series || []), ...extra];
      option.legend = { ...(option.legend || {}), show: true };
    }
  }
  option.aria = { enabled: true, ...(config.options?.altText ? { label: { description: config.options.altText } } : {}) };
  applyLegendOptions(option, {
    legendPos: styleOpts.legendPos,
    legendFontSize: styleOpts.legendFontSize,
    legendColor: styleOpts.legendColor,
    seriesLabels: Object.fromEntries((styleOpts.seriesOverrides || []).filter((s) => s.name && s.label).map((s) => [s.name, s.label])),
    seriesHidden: (styleOpts.seriesOverrides || []).filter((s) => s.name && s.hidden).map((s) => s.name),
  });
  const ttCustomOn = styleOpts.tooltip && (styleOpts.tooltip.custom || styleOpts.tooltip.header || (styleOpts.tooltip.labels || []).some((l) => l.name && l.label));
  if (ttCustomOn) {
    option.tooltip = { ...(option.tooltip || {}), confine: true, formatter: richTooltipFormatter({ data: dataC, spec, config: effConfig, ttCfg: styleOpts.tooltip }) };
  }
  // Item 181: tooltip visual styling
  if (styleOpts.tooltipStyle && Object.keys(styleOpts.tooltipStyle).length) applyTooltipStyle(option, styleOpts.tooltipStyle);
  // Items 168 + cross-highlight: dim non-matching data points
  if (activeHighlights.length) applyHighlightToOption(option, spec, activeHighlights);
  // Item 19: ECharts built-in accessibility — decal patterns (colour-independent series) + reduced motion
  if (a11yPrefs.chart_patterns) option.aria = { ...(option.aria || {}), enabled: true, decal: { show: true } };
  if (typeof document !== "undefined" && document.documentElement.getAttribute("data-a11y-motion") === "reduce") option.animation = false;
  // Item 7: restore the remembered scroll window for this drill level (set on drill-up)
  if (Array.isArray(option.dataZoom) && zoomMemo.current[level]) {
    const { start, end } = zoomMemo.current[level];
    option.dataZoom = option.dataZoom.map((z) => ({ ...z, start, end }));
  }
  const renderer = CANVAS_TYPES.includes(type) ? "canvas" : "svg";
  const insightLines = insightOn ? buildInsights({ type, data: dataC, spec, options: styleOpts, isTime: isTimeAxis }) : [];
  return (
    <div className="h-full w-full flex flex-col relative">
      {drill.length > 0 && <DrillBar drill={drill} onUp={drillUp} onReset={drillReset} />}
      <div className="flex-1 min-h-0">
        <ReactECharts ref={chartRef} option={option} style={{ height: "100%", width: "100%", minHeight: 100 }} opts={{ renderer }} notMerge onEvents={drillEvents} />
      </div>
      {insightOn && <InsightStrip lines={insightLines} onAskAi={aiOk ? () => setExplainOpen(true) : null} />}
      <ChartA11y widget={widget} data={dataR} type={type} />
      <ChartKeyboardNav widget={widget} data={dataC} type={type} chartRef={chartRef}
        onActivate={(params, host) => {
          const r = host?.getBoundingClientRect?.();
          const ev = { clientX: r ? r.left + r.width / 2 : 0, clientY: r ? r.top + r.height / 2 : 0, preventDefault: () => {} };
          (drillEvents.click || drillEvents.dblclick || drillEvents.contextmenu)?.({ ...params, event: { event: ev } });
        }} />
      {dtMenu && <DrillTargetMenu menu={dtMenu} onClose={() => setDtMenu(null)} />}{dtModal && <DrillthroughModal modal={dtModal} onClose={() => setDtModal(null)} />}
      <RefreshingBadge show={loading} />
      <EngineBadge data={data} />
      {insightShow && <InsightToggle on={insightOn} onToggle={toggleInsight} right={aiOk ? "3.75rem" : "0.25rem"} />}
      <ExplainButton widget={widget} filters={allFilters} show={aiOk} open={explainOpen} setOpen={setExplainOpen} />
      <AiDrillButton widget={widget} show={aiOk} Renderer={WidgetRenderer} />
    </div>
  );
}

// Item 193.10: subtle indicator overlaid while a loaded widget refreshes (data stays visible).
function RefreshingBadge({ show }) {
  if (!show) return null;
  return (
    <div data-testid="widget-refreshing" className="absolute top-1 left-1 z-20 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[color:var(--surface)]/90 border border-[color:var(--glass-border)] shadow-sm pointer-events-none">
      <Loader2 className="w-3 h-3 animate-spin text-[color:var(--brand-ocean)]" />
      <span className="text-[9px] text-[color:var(--ink-3)]">Refreshing</span>
    </div>
  );
}

function ExplainButton({ widget, filters, show, open, setOpen, hideBtn = false }) {
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [driverDim, setDriverDim] = useState(null);
  const started = useRef(false);
  const run = useCallback(async () => {
    setLoading(true); setText("");
    try {
      const { data } = await api.post("/ai/explain-widget", {
        dataset_id: widget.dataset_id, config: widget.config || {},
        filters: (filters || []).map(({ __src, ...r }) => r),
        title: widget.title || "", widget_type: widget.type,
      });
      setText(data.explanation || "No explanation generated.");
      setDriverDim(data.driver_dimension);
    } catch (e) {
      setText(e?.response?.data?.detail || "Explanation failed. Check your AI settings.");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget.dataset_id, widget.type, widget.title, JSON.stringify(filters || [])]);
  // Opened either by this button or by "Ask AI for more" in the insight line.
  useEffect(() => {
    if (!open) { started.current = false; return; }
    if (started.current) return;
    started.current = true;
    run();
  }, [open, run]);
  if (!show || !widget.dataset_id) return null;
  return (
    <>
      {!hideBtn && (
        <button data-testid="explain-widget-btn" onClick={() => setOpen(true)} title="Explain this chart (AI)"
          className="absolute top-0.5 right-1 z-10 w-6 h-6 rounded-full flex items-center justify-center text-[color:var(--brand-mint)] opacity-40 hover:opacity-100 hover:bg-[color:rgba(255,107,53,0.12)] transition-all">
          <Sparkles className="w-3.5 h-3.5" />
        </button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Sparkles className="w-5 h-5 text-[color:var(--brand-mint)]" />{widget.title || "This chart"} — explained</DialogTitle>
            <DialogDescription>AI driver analysis for the exact filtered view you're looking at{driverDim ? ` (drilled by ${driverDim})` : ""}.</DialogDescription>
          </DialogHeader>
          <div data-testid="explain-widget-text" className="py-1 text-sm leading-relaxed text-[color:var(--ink-2)] min-h-[80px]">
            {loading ? (
              <div className="flex items-center gap-2 text-[color:var(--ink-3)]"><Loader2 className="w-4 h-4 animate-spin" />Analyzing this view…</div>
            ) : text}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function EngineBadge({ data }) {
  const calcErr = (data && data.meta && data.meta.calc_errors) || null;
  const calcKeys = calcErr ? Object.keys(calcErr) : [];
  if (!data || (!data.engine && data.cached === undefined)) {
    return calcKeys.length ? <CalcErrorBadge errors={calcErr} /> : null;
  }
  const pushdown = data.engine === "sql-pushdown";
  const serverPivot = data.engine === "server-pivot";
  const modelJoin = data.engine === "model-join";
  const federated = data.engine === "duckdb-federated";
  const warns = (data.meta && data.meta.warnings) || [];
  const label = pushdown ? "DB pushdown" : serverPivot ? "server pivot" : modelJoin ? (warns.length ? "model join ⚠" : "model join") : federated ? "cross-source" : data.cached ? "cache" : "in-memory";
  const title = pushdown ? "Aggregated inside the source database (SQL pushdown)"
    : serverPivot ? "Subtotals computed exactly by the server-side pivot engine"
      : federated ? "Each source is queried separately, then combined by the cross-source SQL engine"
        : modelJoin ? (warns.length ? warns.join(" ") : "Joined across datasets via Model relationships")
        : data.cached ? "Served from the query cache" : "Computed in-memory by the analytics engine";
  return (
    <>
      {calcKeys.length ? <CalcErrorBadge errors={calcErr} /> : null}
      <span data-testid="engine-badge" title={title}
        className={"absolute bottom-0.5 right-1 z-10 text-[9px] leading-none px-1.5 py-0.5 rounded-full pointer-events-none select-none " +
          (pushdown || serverPivot ? "bg-[color:rgba(0,194,168,0.15)] text-[color:var(--success)]"
            : modelJoin || federated ? "bg-[color:rgba(24,169,135,0.14)] text-[color:var(--brand-mint)]"
              : data.cached ? "bg-[color:rgba(23,112,138,0.10)] text-[color:var(--brand-ocean)] opacity-80"
                : "bg-[color:var(--surface-2)] text-[color:var(--ink-3)] opacity-70")}>
        {pushdown ? "⚡ " : modelJoin || federated ? "⛓ " : ""}{label}
      </span>
    </>
  );
}

// A calculated field/measure that failed at query time — explains the blank instead of hiding it.
function CalcErrorBadge({ errors }) {
  const lines = Object.entries(errors || {}).map(([k, v]) => `${k}: ${v}`);
  if (!lines.length) return null;
  return (
    <span data-testid="widget-calc-error" title={`Calculated field problem — ${lines.join(" · ")}`}
      className="absolute bottom-0.5 left-1 z-10 text-[9px] leading-none px-1.5 py-0.5 rounded-full select-none cursor-help
        bg-[color:rgba(226,75,91,0.14)] text-[color:var(--danger)] font-semibold">
      ƒx error{lines.length > 1 ? ` ×${lines.length}` : ""}
    </span>
  );
}

function DrillBar({ drill, onUp, onReset, note }) {
  return (
    <div data-testid="drill-bar" className="flex items-center gap-1 px-1 pb-1.5 text-[11px] text-[color:var(--ink-3)] flex-wrap">
      <button onClick={onReset} className="hover:text-[color:var(--brand-ocean)] font-medium">All</button>
      {drill.map((d, i) => (
        <span key={`${d.value}-${i}`} className="flex items-center gap-1">
          <ChevronRight className="w-3 h-3" />
          <span className="text-[color:var(--ink-2)] font-medium">{d.label || d.value}</span>
        </span>
      ))}
      {note && <span className="ml-1 italic opacity-70">({note})</span>}
      <button onClick={onUp} data-testid="drill-up" className="ml-auto flex items-center gap-1 px-1.5 py-0.5 rounded-md hover:bg-[color:var(--surface-2)] text-[color:var(--brand-ocean)]"><CornerUpLeft className="w-3 h-3" />Back</button>
    </div>
  );
}

function Center({ children }) {
  return <div className="h-full w-full flex items-center justify-center">{children}</div>;
}

// Item 167: markdown-lite for the Text/Note widget (#/## headings, - bullets, **bold**, *italic*)
function inlineMd(text, keyBase) {
  const parts = [];
  const re = /(\*\*[^*]+\*\*|\*[^*]+\*|\{(?:green|red|gray):[^}]*\})/g;
  const DELTA_COLORS = { green: "#16A34A", red: "#DC2626", gray: "var(--ink-3)" };
  let last = 0, m, i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) parts.push(<b key={`${keyBase}-${i++}`}>{tok.slice(2, -2)}</b>);
    else if (tok.startsWith("{")) {
      const ci = tok.indexOf(":");
      const kind = tok.slice(1, ci);
      parts.push(<span key={`${keyBase}-${i++}`} data-testid={`delta-${kind}`} style={{ color: DELTA_COLORS[kind], fontWeight: 600 }}>{tok.slice(ci + 1, -1)}</span>);
    }
    else parts.push(<i key={`${keyBase}-${i++}`}>{tok.slice(1, -1)}</i>);
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}
function renderMarkdownLite(text) {
  return String(text).split("\n").map((line, i) => {
    if (line.startsWith("## ")) return <div key={i} className="font-display font-semibold" style={{ fontSize: "1.25em" }}>{inlineMd(line.slice(3), i)}</div>;
    if (line.startsWith("# ")) return <div key={i} className="font-display font-bold" style={{ fontSize: "1.55em" }}>{inlineMd(line.slice(2), i)}</div>;
    if (line.startsWith("- ")) return <div key={i} className="flex gap-1.5"><span>•</span><span>{inlineMd(line.slice(2), i)}</span></div>;
    if (!line.trim()) return <div key={i} style={{ height: "0.6em" }} />;
    return <div key={i}>{inlineMd(line, i)}</div>;
  });
}

// Item 193.13: skip re-renders unless data-relevant props actually changed.
// Function props are intentionally excluded (parents use functional setState, so
// behaviour is stable); the widget re-renders whenever its config/filters change.
const jsonEq = (a, b) => a === b || JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
function widgetPropsEqual(prev, next) {
  return prev.publicToken === next.publicToken
    && prev.refreshToken === next.refreshToken
    && prev.pivotEngine === next.pivotEngine
    && prev.chartFont === next.chartFont
    && prev.lang === next.lang
    && prev.dashboardId === next.dashboardId
    && prev.slicerValue === next.slicerValue
    && jsonEq(prev.filterValue, next.filterValue)
    && (prev.onConfigChange == null) === (next.onConfigChange == null)
    && (prev.onCrossFilter == null) === (next.onCrossFilter == null)
    && (prev.onDrillthrough == null) === (next.onDrillthrough == null)
    && jsonEq(prev.widget, next.widget)
    && jsonEq(prev.globalFilters, next.globalFilters)
    && jsonEq(prev.highlightFilters, next.highlightFilters)
    && jsonEq(prev.multiFilterValues, next.multiFilterValues)
    && jsonEq(prev.whatIfParams, next.whatIfParams)
    && jsonEq(prev.whatIfBaseline, next.whatIfBaseline)
    && jsonEq(prev.whatIfDefs, next.whatIfDefs)
    && jsonEq(prev.whatIfScenarios, next.whatIfScenarios)
    && prev.onConfigPatch === next.onConfigPatch
    && prev.whatIfCompare === next.whatIfCompare;
}
const MemoWidgetRenderer = React.memo(WidgetRendererInner, widgetPropsEqual);
